/**
 * Platform-admin billing settings:
 *  - Zoho Payments credentials (account_id / api_key / signing_key) — write-only
 *  - Demo mode toggle
 *  - Per-org limit overrides
 */
import { useEffect, useMemo, useState } from "react";
import { api, extractError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/app/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { CreditCard, MagnifyingGlass, CaretUp, CaretDown } from "@phosphor-icons/react";

export default function PlatformBillingPage() {
    const [z, setZ] = useState(null);
    const [draft, setDraft] = useState({ account_id: "", api_key: "", signing_key: "", oauth_client_id: "", oauth_client_secret: "", oauth_refresh_token: "" });
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);

    const runTestConnection = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const { data } = await api.post("/billing/platform/zoho-payments/test", {});
            setTestResult(data);
            if (data.ok) toast.success("Zoho connectivity OK");
            else toast.error(data.message || "Connectivity test failed — see results below");
        } catch (e) {
            setTestResult({ ok: false, message: extractError(e), steps: [] });
            toast.error(extractError(e));
        } finally {
            setTesting(false);
        }
    };
    const [saving, setSaving] = useState(false);

    const [orgs, setOrgs] = useState([]);
    const [orgBilling, setOrgBilling] = useState(null);
    const [overrideOrg, setOverrideOrg] = useState(null);
    const [overrideDraft, setOverrideDraft] = useState({ users: "", forms: "", records: "", storage_gb: "" });

    const [search, setSearch] = useState("");
    const [sortBy, setSortBy] = useState("name");
    const [sortDir, setSortDir] = useState(1);
    const [page, setPage] = useState(1);
    const pageSize = 10;

    const load = async () => {
        try {
            const [r, o] = await Promise.all([
                api.get("/billing/platform/zoho-payments"),
                api.get("/platform/organizations"),
            ]);
            setZ(r.data); setOrgs(o.data);
        } catch (e) { toast.error(extractError(e)); }
    };
    useEffect(() => { load(); }, []);

    const saveZoho = async () => {
        setSaving(true);
        try {
            const payload = { demo_mode: z.demo_mode };
            if (draft.account_id) payload.account_id = draft.account_id;
            if (draft.api_key) payload.api_key = draft.api_key;
            if (draft.signing_key) payload.signing_key = draft.signing_key;
            if (draft.oauth_client_id) payload.oauth_client_id = draft.oauth_client_id;
            if (draft.oauth_client_secret) payload.oauth_client_secret = draft.oauth_client_secret;
            if (draft.oauth_refresh_token) payload.oauth_refresh_token = draft.oauth_refresh_token;
            const { data } = await api.put("/billing/platform/zoho-payments", payload);
            setZ(data);
            setDraft({ account_id: "", api_key: "", signing_key: "", oauth_client_id: "", oauth_client_secret: "", oauth_refresh_token: "" });
            toast.success("Zoho Payments settings saved");
        } catch (e) { toast.error(extractError(e)); }
        finally { setSaving(false); }
    };

    const openOverride = async (o) => {
        try {
            const { data } = await api.get(`/billing/platform/orgs/${o.id}`);
            setOrgBilling(data); setOverrideOrg(o);
            setOverrideDraft({
                users: data.limits.overrides?.users || "",
                forms: data.limits.overrides?.forms || "",
                records: data.limits.overrides?.records || "",
                storage_gb: data.limits.overrides?.storage_gb || "",
            });
        } catch (e) { toast.error(extractError(e)); }
    };

    const saveOverride = async (clear = false) => {
        try {
            const payload = clear ? { clear: true } : {
                users: overrideDraft.users === "" ? null : Number(overrideDraft.users),
                forms: overrideDraft.forms === "" ? null : Number(overrideDraft.forms),
                records: overrideDraft.records === "" ? null : Number(overrideDraft.records),
                storage_gb: overrideDraft.storage_gb === "" ? null : Number(overrideDraft.storage_gb),
            };
            await api.put(`/billing/platform/orgs/${overrideOrg.id}/limits`, payload);
            toast.success(clear ? "Override cleared" : "Override saved");
            setOverrideOrg(null);
        } catch (e) { toast.error(extractError(e)); }
    };

    const filteredOrgs = useMemo(() => {
        let arr = orgs;
        if (search) {
            const q = search.toLowerCase();
            arr = arr.filter((o) => o.name.toLowerCase().includes(q) || (o.slug || "").toLowerCase().includes(q));
        }
        arr = [...arr].sort((a, b) => {
            const av = a[sortBy] ?? "";
            const bv = b[sortBy] ?? "";
            if (typeof av === "number") return (av - bv) * sortDir;
            return String(av).localeCompare(String(bv)) * sortDir;
        });
        return arr;
    }, [orgs, search, sortBy, sortDir]);
    const totalPages = Math.max(1, Math.ceil(filteredOrgs.length / pageSize));
    const pageRows = useMemo(
        () => filteredOrgs.slice((page - 1) * pageSize, page * pageSize),
        [filteredOrgs, page]
    );

    const toggleSort = (col) => {
        if (sortBy === col) setSortDir(sortDir === 1 ? -1 : 1);
        else { setSortBy(col); setSortDir(1); }
        setPage(1);
    };
    const SortIcon = ({ col }) =>
        sortBy === col ? (sortDir === 1 ? <CaretUp size={10} className="inline ml-1" /> : <CaretDown size={10} className="inline ml-1" />) : null;

    if (!z) return <PageContainer><div className="text-sm text-muted-foreground">Loading…</div></PageContainer>;

    return (
        <PageContainer>
            <PageHeader
                overline="Platform"
                title="Zoho Payments"
                description="Configure Zoho Payments credentials (India region) and override per-org subscription limits."
                testid="platform-billing-header"
            />

            <div className="mb-8" data-testid="zoho-wrapper">
                <div className="brut-section p-6" data-testid="zoho-settings">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-2">
                            <CreditCard size={20} weight="duotone" />
                            <h2 className="text-xl font-bold tracking-tight">Zoho Payments (India)</h2>
                        </div>
                        <Badge className={`rounded-sm ${z.configured && !z.demo_mode ? "bg-success" : "bg-yellow-500"}`} data-testid="zoho-status-badge">
                            {z.demo_mode ? "DEMO mode" : z.configured ? "LIVE" : "Not configured"}
                        </Badge>
                    </div>
                    <div className="flex items-center justify-between mb-4 border border-border rounded-sm p-3">
                        <div>
                            <div className="text-sm font-medium">Demo mode</div>
                            <div className="text-xs text-muted-foreground">When ON, pack purchases are simulated — no Zoho call.</div>
                        </div>
                        <Switch
                            checked={!!z.demo_mode}
                            onCheckedChange={(v) => setZ({ ...z, demo_mode: v })}
                            data-testid="demo-mode-toggle"
                        />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                            <Label className="label-overline">Account ID</Label>
                            <Input
                                value={draft.account_id} onChange={(e) => setDraft({ ...draft, account_id: e.target.value })}
                                placeholder={z.account_id_masked ? `Current: …${z.account_id_masked} (leave blank to keep)` : "60xxxxxxxxxx"}
                                className="mt-2 rounded-sm font-mono"
                                data-testid="zoho-account-id-input"
                            />
                            <p className="text-[10px] text-muted-foreground mt-1">From your Zoho Payments dashboard URL (e.g. <code>payments.zoho.in/app/60123…/dashboard</code>).</p>
                        </div>
                        <div>
                            <Label className="label-overline">API Key</Label>
                            <Input
                                type="password" value={draft.api_key} onChange={(e) => setDraft({ ...draft, api_key: e.target.value })}
                                placeholder="leave blank to keep current"
                                className="mt-2 rounded-sm font-mono"
                                data-testid="zoho-api-key-input"
                            />
                            <p className="text-[10px] text-muted-foreground mt-1">Settings → Developer Space → Authentication Keys → API Key.</p>
                        </div>
                        <div className="sm:col-span-2">
                            <Label className="label-overline">Signing Key</Label>
                            <Input
                                type="password" value={draft.signing_key} onChange={(e) => setDraft({ ...draft, signing_key: e.target.value })}
                                placeholder={z.signing_key_set ? "•••••••• (leave blank to keep)" : "leave blank to keep current"}
                                className="mt-2 rounded-sm font-mono"
                                data-testid="zoho-signing-key-input"
                            />
                            <p className="text-[10px] text-muted-foreground mt-1">Settings → Developer Space → Authentication Keys → Signing Key. Used to HMAC-verify the widget&apos;s payment confirmation.</p>
                        </div>
                    </div>

                    <div className="border-t border-border mt-5 pt-4">
                        <Label className="label-overline">Server-side OAuth (Self Client)</Label>
                        <p className="text-[11px] text-muted-foreground mt-1">
                            Zoho Payments&apos; server API requires OAuth. Register a Self Client at
                            {" "}<a href="https://api-console.zoho.in/add?client_type=ORG" target="_blank" rel="noopener noreferrer" className="underline text-primary">api-console.zoho.in</a>{" "}
                            with scope <code className="font-mono text-[10px] bg-secondary px-1 rounded-sm">ZohoPay.payments.CREATE,ZohoPay.payments.READ</code>,
                            then exchange a 1-minute authorization code for a refresh token via
                            {" "}<code className="font-mono text-[10px] bg-secondary px-1 rounded-sm">POST https://accounts.zoho.in/oauth/v2/token</code>. See /help → Billing for the full walkthrough.
                        </p>
                        <div className="grid sm:grid-cols-2 gap-3 mt-3">
                            <div>
                                <Label className="label-overline">OAuth Client ID</Label>
                                <Input
                                    type="password" value={draft.oauth_client_id} onChange={(e) => setDraft({ ...draft, oauth_client_id: e.target.value })}
                                    placeholder={z.oauth_client_id_set ? "•••••••• (leave blank to keep)" : "1000.XXXXXXXXXXXXXXXX"}
                                    className="mt-2 rounded-sm font-mono"
                                    data-testid="zoho-oauth-client-id-input"
                                />
                            </div>
                            <div>
                                <Label className="label-overline">OAuth Client Secret</Label>
                                <Input
                                    type="password" value={draft.oauth_client_secret} onChange={(e) => setDraft({ ...draft, oauth_client_secret: e.target.value })}
                                    placeholder={z.oauth_client_secret_set ? "•••••••• (leave blank to keep)" : "from Self Client app"}
                                    className="mt-2 rounded-sm font-mono"
                                    data-testid="zoho-oauth-client-secret-input"
                                />
                            </div>
                            <div className="sm:col-span-2">
                                <Label className="label-overline">OAuth Refresh Token</Label>
                                <Input
                                    type="password" value={draft.oauth_refresh_token} onChange={(e) => setDraft({ ...draft, oauth_refresh_token: e.target.value })}
                                    placeholder={z.oauth_refresh_token_set ? "•••••••• (leave blank to keep)" : "1000.XXXXXXXXXXXXXXXXXXXX (long-lived)"}
                                    className="mt-2 rounded-sm font-mono"
                                    data-testid="zoho-oauth-refresh-token-input"
                                />
                                <p className="text-[10px] text-muted-foreground mt-1">Generated once via the authorization-code exchange. Doesn&apos;t expire — store securely.</p>
                            </div>
                        </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-3">
                        All credentials are required for LIVE mode. Keep demo mode ON until OAuth is fully configured and tested.
                    </p>
                    <div className="flex flex-wrap gap-2 mt-4">
                        <Button
                            onClick={saveZoho} disabled={saving}
                            className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground"
                            data-testid="save-zoho-btn"
                        >
                            {saving ? "Saving…" : "Save settings"}
                        </Button>
                        <Button
                            type="button" variant="outline"
                            onClick={runTestConnection} disabled={testing || !z.configured}
                            className="rounded-sm"
                            data-testid="zoho-test-connection-btn"
                            title={z.configured ? "Verify OAuth + create a real ₹1 test payment session at Zoho" : "Save all credentials first"}
                        >
                            {testing ? "Testing…" : "Test connection"}
                        </Button>
                    </div>
                    {testResult && (
                        <div className={`mt-4 brut-section p-3 ${testResult.ok ? "border-emerald-500/40 bg-emerald-500/5" : "border-destructive/40 bg-destructive/5"}`} data-testid="zoho-test-result">
                            <div className={`text-sm font-medium mb-2 ${testResult.ok ? "text-emerald-600" : "text-destructive"}`}>
                                {testResult.ok ? "✓ " : "✗ "}{testResult.message || (testResult.ok ? "All checks passed." : "Test failed.")}
                            </div>
                            <ul className="space-y-1.5">
                                {(testResult.steps || []).map((s) => (
                                    <li key={s.step} className="text-xs flex items-start gap-2" data-testid={`zoho-test-step-${s.step}`}>
                                        <span className={s.ok ? "text-emerald-600" : "text-destructive"}>{s.ok ? "✓" : "✗"}</span>
                                        <span>
                                            <code className="font-mono text-[10px] bg-secondary px-1 rounded-sm">{s.step}</code>
                                            <span className="ml-2 text-muted-foreground">{s.detail}</span>
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>

            <div className="brut-section overflow-hidden" data-testid="orgs-billing-table">
                <div className="px-6 py-4 border-b border-border flex items-center justify-between gap-4 flex-wrap">
                    <div>
                        <h2 className="text-xl font-bold tracking-tight">Per-org limits</h2>
                        <p className="text-sm text-muted-foreground mt-1">Override pack-computed limits for any organization.</p>
                    </div>
                    <div className="relative w-full sm:w-80">
                        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Search by name or slug…"
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                            className="rounded-sm pl-9 h-9"
                            data-testid="orgs-billing-search"
                        />
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead><tr>
                            <th className="data-header text-left cursor-pointer select-none" onClick={() => toggleSort("name")} data-testid="sort-name">
                                Organization<SortIcon col="name" />
                            </th>
                            <th className="data-header text-left cursor-pointer select-none" onClick={() => toggleSort("user_count")} data-testid="sort-users">
                                Users<SortIcon col="user_count" />
                            </th>
                            <th className="data-header text-left cursor-pointer select-none" onClick={() => toggleSort("form_count")} data-testid="sort-forms">
                                Forms<SortIcon col="form_count" />
                            </th>
                            <th className="data-header"></th>
                        </tr></thead>
                        <tbody>
                            {pageRows.length === 0 ? (
                                <tr><td colSpan={4} className="data-cell text-center text-muted-foreground">No organizations match.</td></tr>
                            ) : pageRows.map((o) => (
                                <tr key={o.id} className="hover:bg-secondary/50" data-testid={`org-billing-row-${o.id}`}>
                                    <td className="data-cell font-medium">{o.name}<div className="font-mono text-[10px] text-muted-foreground">{o.slug}</div></td>
                                    <td className="data-cell font-mono text-sm">{o.user_count}</td>
                                    <td className="data-cell font-mono text-sm">{o.form_count}</td>
                                    <td className="data-cell text-right">
                                        <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => openOverride(o)} data-testid={`override-org-${o.id}`}>
                                            Override limits
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="flex items-center justify-between px-6 py-3 border-t border-border text-sm">
                    <div className="text-muted-foreground">
                        Total: {filteredOrgs.length} · Page {page}/{totalPages}
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" className="rounded-sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="orgs-billing-prev">Previous</Button>
                        <Button variant="outline" size="sm" className="rounded-sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="orgs-billing-next">Next</Button>
                    </div>
                </div>
            </div>

            <Dialog open={!!overrideOrg} onOpenChange={(v) => !v && setOverrideOrg(null)}>
                <DialogContent className="rounded-sm" data-testid="override-dialog">
                    <DialogHeader>
                        <DialogTitle>Override limits for {overrideOrg?.name}</DialogTitle>
                        <DialogDescription>Leave blank to clear that individual cap. Click "Clear all overrides" to revert entirely to pack-computed limits.</DialogDescription>
                    </DialogHeader>
                    {orgBilling && (
                        <div className="space-y-4">
                            <div className="text-xs font-mono text-muted-foreground bg-secondary rounded-sm p-3">
                                Current usage: users={orgBilling.usage.users} forms={orgBilling.usage.forms} records={orgBilling.usage.records} · packs={orgBilling.limits.expansion_pack_count}
                            </div>
                            {[
                                ["users", "Users cap"],
                                ["forms", "Forms cap"],
                                ["records", "Records cap"],
                                ["storage_gb", "Storage GB cap"],
                            ].map(([k, label]) => (
                                <div key={k}>
                                    <Label className="label-overline">{label}</Label>
                                    <Input
                                        type="number" min={0}
                                        value={overrideDraft[k]} onChange={(e) => setOverrideDraft({ ...overrideDraft, [k]: e.target.value })}
                                        className="mt-2 rounded-sm font-mono"
                                        data-testid={`override-${k}-input`}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                    <DialogFooter className="gap-2">
                        <Button variant="outline" className="rounded-sm" onClick={() => saveOverride(true)} data-testid="clear-override-btn">Clear all overrides</Button>
                        <Button className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => saveOverride(false)} data-testid="save-override-btn">Save override</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </PageContainer>
    );
}
